import React, { useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonModal,
  IonToggle,
  IonSpinner,
} from "@ionic/react";
import { useAuthUser } from "../../context/UserManager";
import { useUserData } from "../../context/UserDataFetcher";
import AddUserModel from "../../components/AddEmailModal/AddAuthorizedEmail"; // Ensure the path to AddUserModal is correct
import "./Settings.css";
import { useOfflineSync } from "../../context/OfflineSyncProvider";
import MobileAppBar from "../../components/AppBar/MobileAppBar";
import { useDeveloperMode } from "../../context/DeveloperModeContext";
import { useUserControls } from "../../context/UserControlsProvider";
import { deleteAccount, AccountDeletionError } from "../../api/account/deleteAccount";
import { getAuth, signOut } from "firebase/auth";
import { app } from "../../firebaseConfig";
import { useHistory } from "react-router";

const Settings: React.FC = () => {
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const { authUser } = useAuthUser();
  const userData = useUserData();
  const { isForcedOffline, setForcedOffline, featureEnabled } =
    useOfflineSync();
  const {
    developerMode,
    toggleDeveloperMode,
    signatureOverrideActive,
    setSignatureOverrideActive,
  } = useDeveloperMode();
  const { showSnackbar } = useUserControls();
  const history = useHistory();
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const isAccountOwner = userData?.userType?.toLowerCase() === "account owner";

  const handleAccountDeleted = async () => {
    try {
      await signOut(getAuth(app));
    } catch (error) {
      console.error("[Settings] Failed to sign out after account deletion", error);
    } finally {
      history.replace("/");
    }
  };

  const confirmDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await deleteAccount();
      showSnackbar?.({
        message: "Your account has been permanently deleted.",
        severity: "success",
        duration: 6000,
      });
      setShowDeleteAccountModal(false);
      await handleAccountDeleted();
    } catch (error) {
      console.error("[Settings] Failed to delete account", error);
      const message =
        error instanceof AccountDeletionError
          ? error.message
          : "Unable to delete account. Please try again.";
      showSnackbar?.({
        message,
        severity: "error",
        duration: 6000,
      });
    } finally {
      setDeletingAccount(false);
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <MobileAppBar title="Settings" position="static" />
      </IonHeader>
      <IonContent>
        <IonList>
          {featureEnabled && (
            <IonItem lines="full">
              <IonLabel>
                <h2>Offline Mode</h2>
                <p>Simulate offline conditions to test queued sync.</p>
              </IonLabel>
              <IonToggle
                slot="end"
                checked={isForcedOffline}
                onIonChange={(event) => {
                  void setForcedOffline(event.detail.checked);
                }}
              />
            </IonItem>
          )}
          <IonItem lines="full">
            <IonLabel>
              <h2>Developer Mode</h2>
              <p>Expose experimental tools for QA and internal testing.</p>
            </IonLabel>
            <IonToggle
              slot="end"
              checked={developerMode}
              onIonChange={(event) => {
                if (event.detail.checked !== developerMode) {
                  toggleDeveloperMode();
                }
              }}
            />
          </IonItem>
          {developerMode && (
            <IonItem lines="full">
              <IonLabel>
                <h3>Emergency signature override</h3>
                <p>Force-enable shipment signatures when a shipment is not fully processed.</p>
              </IonLabel>
              <IonButton
                slot="end"
                color={signatureOverrideActive ? "danger" : "primary"}
                onClick={() => setSignatureOverrideActive(!signatureOverrideActive)}
              >
                {signatureOverrideActive ? "Disable" : "Force enable"}
              </IonButton>
            </IonItem>
          )}
          {developerMode && authUser && (
            <>
              <IonItem>
                <IonLabel>User ID: {authUser.uid}</IonLabel>
              </IonItem>
              {isAccountOwner && (
                <IonItem>
                  <IonButton
                    expand="block"
                    onClick={() => setShowAddUserModal(true)}
                  >
                    Add User
                  </IonButton>
                </IonItem>
              )}
            </>
          )}
          <IonItem
            button
            lines="full"
            detail={false}
            onClick={() => setShowDeleteAccountModal(true)}
          >
            <IonLabel className="delete-account-label">
              Delete Account
            </IonLabel>
          </IonItem>
        </IonList>
        {isAccountOwner && (
          <IonModal
            isOpen={showAddUserModal}
            onDidDismiss={() => setShowAddUserModal(false)}
          >
            <AddUserModel
              isOpen={showAddUserModal}
              onClose={() => setShowAddUserModal(false)}
            />
          </IonModal>
        )}
        <IonModal
          isOpen={showDeleteAccountModal}
          onDidDismiss={() => {
            if (!deletingAccount) {
              setShowDeleteAccountModal(false);
            }
          }}
        >
          <div className="delete-account-modal">
            <IonHeader>
              <IonToolbar>
                <IonTitle>Delete Account</IonTitle>
              </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
              <div className="delete-account-modal__content">
                <p>
                  Deleting your account will permanently remove your profile
                  and all associated data from Cabinet Logger. This action
                  cannot be undone.
                </p>
                <p className="delete-account-modal__note">
                  If you simply want to stop using the app for now, you can
                  sign out instead.
                </p>
              </div>
              <div className="delete-account-modal__actions">
                <IonButton
                  fill="outline"
                  color="medium"
                  expand="block"
                  onClick={() => setShowDeleteAccountModal(false)}
                  disabled={deletingAccount}
                >
                  Cancel
                </IonButton>
                <IonButton
                  color="danger"
                  expand="block"
                  onClick={() => void confirmDeleteAccount()}
                  disabled={deletingAccount}
                >
                  {deletingAccount ? (
                    <span className="delete-account-modal__loading">
                      <IonSpinner name="crescent" />
                      Deleting...
                    </span>
                  ) : (
                    "Delete"
                  )}
                </IonButton>
              </div>
            </IonContent>
          </div>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default Settings;
